/**
 * Cooling chips from the venue's recipes — what the kitchen cooks and cools
 * ("Beef stock", "Chicken biryani 2"), not a copied share title or an added time.
 */

import { FOOD_SAFETY_SECTION } from 'src/constants/foodSafety'
import { recipeTitleKey } from 'src/utils/recipeDuplicateTitles'
import { isRecipeArchived } from 'src/utils/recipeMonitoring'
import { tidyDisplayTitle } from 'src/utils/venueName'

export const FOOD_SAFETY_RECIPE_SUGGEST_SECTIONS = Object.freeze([
  FOOD_SAFETY_SECTION.COOLING,
])

export function isFoodSafetyRecipeSuggestSection(section) {
  return FOOD_SAFETY_RECIPE_SUGGEST_SECTIONS.includes(section)
}

const recipeTitle = recipe => String(recipe?.title || '').trim()

const COPY_MARK_RE = /\s*\((?:copied|shared with\b[^)]*)\)/gi

/** "Chicken biryani 2", without a share or copy mark. */
export function foodSafetyRecipeKitchenName(title) {
  const tidy = tidyDisplayTitle(title)
  if (typeof tidy !== 'string') return ''
  return tidy.replace(COPY_MARK_RE, '').replace(/\s+/g, ' ').trim()
}

const isCopyTitle = title =>
  /\((?:copied|shared with\b)/i.test(String(title || ''))

/** Ids of recipes used inside another recipe — prep batches, cooled first. */
const subRecipeIds = recipes => {
  const ids = new Set()
  for (const recipe of recipes || []) {
    for (const line of recipe?.recipeIngredients || []) {
      const id = line?.recipeId || line?.recipe?._id || line?.recipe
      if (id && typeof id !== 'object') ids.add(String(id))
    }
  }
  return ids
}

/**
 * Recipe titles not already a check in this section. Prep batches (used in
 * another recipe) first, then A–Z. A stored recipe (monitored === false)
 * stays off the chips. Copies of one kitchen name are a single chip,
 * with no added-time cue.
 *
 * @returns {Array<{ id: string, title: string, cue: string|null, score: number }>}
 */
export function listFoodSafetyRecipeCandidates({
  recipes = [],
  existingDefinitions = [],
  section,
} = {}) {
  if (!isFoodSafetyRecipeSuggestSection(section)) return []
  const existing = new Set(
    (existingDefinitions || [])
      .filter(d => d && d.section === section && d.active !== false)
      .map(d => recipeTitleKey(d.name))
      .filter(Boolean),
  )
  const prep = subRecipeIds(recipes)
  const eligible = []
  for (const recipe of recipes || []) {
    if (recipe?.deleted === true || isRecipeArchived(recipe)) continue
    const title = recipeTitle(recipe)
    const key = recipeTitleKey(title)
    if (!title || !key || existing.has(key)) continue
    eligible.push(recipe)
  }
  const chosen = new Map()
  for (const recipe of eligible) {
    const raw = recipeTitle(recipe)
    const kitchen = foodSafetyRecipeKitchenName(raw)
    const kitchenKey = recipeTitleKey(kitchen)
    if (!kitchen || !kitchenKey) continue
    if (existing.has(recipeTitleKey(raw)) || existing.has(kitchenKey)) continue
    const id = String(recipe?._id || kitchenKey)
    const row = {
      id,
      title: kitchen,
      score: prep.has(id) ? 1 : 0,
      copied: isCopyTitle(raw),
    }
    const prev = chosen.get(kitchenKey)
    if (
      !prev ||
      row.score > prev.score ||
      (row.score === prev.score && prev.copied && !row.copied)
    ) {
      chosen.set(kitchenKey, row)
    }
  }
  const candidates = [...chosen.values()].map(row => ({
    id: `recipe-${row.id}`,
    title: row.title,
    cue: null,
    score: row.score,
  }))
  return candidates.sort(
    (a, b) => b.score - a.score || a.title.localeCompare(b.title),
  )
}
